import type { AnyAction, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

/* ---------------------------------
   Types
---------------------------------- */
export type ThemeName = "theme1" | "theme2" | "theme3";

export interface ThemeState {
  theme: ThemeName;
}

const LS_KEY = "app-theme";

/* ---------------------------------
   DOM Side Effect
---------------------------------- */
export function applyThemeToDOM(theme: ThemeName) {
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  // Enable transition overlay
  root.setAttribute("data-theme-transition", "");

  requestAnimationFrame(() => {
    root.dataset.theme = theme;

    // Remove overlay after animation
    setTimeout(() => {
      root.removeAttribute("data-theme-transition");
    }, 4200);
  });
}

/* ---------------------------------
   Initial State
---------------------------------- */
const initialState: ThemeState = {
  theme: "theme2",
};

/* ---------------------------------
   Slice
---------------------------------- */
const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<ThemeName>) {
      state.theme = action.payload;
    },
  },
});

export const { setTheme } = themeSlice.actions;
export default themeSlice.reducer;

/* ---------------------------------
   Thunks
---------------------------------- */
type ThunkResult<R = void> = ThunkAction<R, any, unknown, AnyAction>;

/** Initialize theme from localStorage */
export const initTheme = (): ThunkResult => (dispatch) => {
  const theme: ThemeName = "theme2";

  try {
    // const saved = localStorage.getItem(LS_KEY) as ThemeName | null;
    // if (saved === "theme1" || saved === "theme2" || saved === "theme3") {
    //   theme = saved;
    // }
  } catch {
    // ignore storage errors
  }

  dispatch(setTheme(theme));
  applyThemeToDOM(theme);
};

/** Change theme, persist it, and apply to DOM */
export const setThemeAndPersist =
  (theme: ThemeName): ThunkResult =>
  (dispatch) => {
    dispatch(setTheme(theme));

    try {
      localStorage.setItem(LS_KEY, theme);
    } catch {
      // ignore storage errors
    }

    applyThemeToDOM(theme);
  };

/* ---------------------------------
   Selector
---------------------------------- */
export const selectTheme = (state: any): ThemeName => state.theme?.theme ?? "theme2";
